import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { usePermissions } from '../App'; // Import the permissions hook
import { reverseTrustTransaction, exportTrustLedgerPDF } from '../api';
import { formatCurrency } from '../utils/currency';

const TrustAccounting = ({ authAxios, clients, cases, invoices, showFormMessage, fetchInvoices, fetchPayments, navigateTo, setNavigateTo, navigateToAuditSupportTrustSummary }) => {
    const permissions = usePermissions();
    const [trustAccounts, setTrustAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [transactions, setTransactions] = useState([]); // This will now store transactions with the running balance
    const [isLoading, setIsLoading] = useState(false);
    const [showAccountForm, setShowAccountForm] = useState(false);
    const [showTransactionForm, setShowTransactionForm] = useState(false);

    const [ledgerDateFilter, setLedgerDateFilter] = useState({
        enabled: false,
        startDate: '',
        endDate: '',
    });

    const [newAccountData, setNewAccountData] = useState({
        client: '',
        account_name: '',
        account_number: '',
        bank_name: '',
    });

    const [newTransactionData, setNewTransactionData] = useState({
        trust_account: '',
        case: '',
        invoice: '',
        transaction_type: 'DEPOSIT',
        amount: '',
        description: '',
        // Group K / third-party disbursement audit fields
        is_third_party_disbursement: false,
        third_party_invoice_number: '',
        third_party_supplier_name: '',
        client_tin: '',
        client_address: '',
    });

    const getClientDisplayName = useCallback((client) => {
        if (!client) return '';
        return client.organization_name || `${client.first_name || ''} ${client.last_name || ''}`.trim();
      }, []);

    const caseInvoices = useMemo(() => {
        if (!newTransactionData.case || !invoices) return [];
        return invoices.filter(inv => 
            inv.case === parseInt(newTransactionData.case) && 
            inv.status !== 'Paid' && 
            inv.status !== 'Void' && 
            inv.status !== 'Cancelled'
        );
    }, [newTransactionData.case, invoices]);

    const fetchTrustAccounts = async () => {
        setIsLoading(true);
        try {
            const response = await authAxios.get('trust-accounts/');
            setTrustAccounts(response.data.results || response.data);
        } catch (error) {
            console.error("Error fetching trust accounts:", error);
            showFormMessage('danger', 'Could not fetch trust accounts.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchTrustAccounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Support deep-linking into a specific trust account ledger.
    useEffect(() => {
        if (!navigateTo || navigateTo.tab !== 'trustAccounting') return;
        if (navigateTo.type !== 'trust_account_ledger' || !navigateTo.id) return;

        const openLedger = async () => {
            setIsLoading(true);
            try {
                const resp = await authAxios.get(`trust-accounts/${navigateTo.id}/`);
                handleAccountSelect(resp.data);

                // If the user came from Annual Audit Support, optionally filter to the same date range.
                if (navigateTo.from === 'annualAuditSupport' && navigateTo.startDate && navigateTo.endDate) {
                    setLedgerDateFilter({
                        enabled: true,
                        startDate: navigateTo.startDate,
                        endDate: navigateTo.endDate,
                    });
                }
            } catch (error) {
                console.error('Failed to open trust ledger:', error);
                showFormMessage('danger', 'Could not open the selected trust account ledger.');
            } finally {
                setIsLoading(false);
                if (typeof setNavigateTo === 'function') setNavigateTo(null);
            }
        };

        openLedger();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigateTo]);

    const displayedTransactions = useMemo(() => {
        if (!ledgerDateFilter.enabled || !ledgerDateFilter.startDate || !ledgerDateFilter.endDate) return transactions;
        const start = ledgerDateFilter.startDate;
        const end = ledgerDateFilter.endDate;
        // transaction_date is YYYY-MM-DD (string); lexicographic compare works.
        return (transactions || []).filter(tx => {
            const d = tx?.transaction_date;
            return d && d >= start && d <= end;
        });
    }, [transactions, ledgerDateFilter]);

    // **MODIFIED: handleAccountSelect to calculate running balance**
    const handleAccountSelect = (account) => {
        setSelectedAccount(account);
        
        // Calculate running balance
        // The API sends transactions newest-first. We work backward from the current balance.
        let currentRunningBalance = parseFloat(account.balance);
        const transactionsWithBalance = (account.transactions || []).map(tx => {
            const txAmount = parseFloat(tx.amount);
            
            // The running balance *for this transaction* is the balance *after* it occurred.
            const txWithBalance = { 
                ...tx, 
                running_balance: currentRunningBalance.toFixed(2) 
            };

            // Now, calculate the balance *before* this transaction for the next loop iteration (which is the previous tx)
            if (tx.transaction_type === 'DEPOSIT') {
                currentRunningBalance -= txAmount;
            } else if (tx.transaction_type === 'WITHDRAWAL') {
                currentRunningBalance += txAmount;
            }
            
            return txWithBalance;
        });

        setTransactions(transactionsWithBalance);
        setNewTransactionData({
            trust_account: account.id,
            case: '',
            invoice: '',
            transaction_type: 'DEPOSIT',
            amount: '',
            description: '',
            is_third_party_disbursement: false,
            third_party_invoice_number: '',
            third_party_supplier_name: '',
            client_tin: '',
            client_address: '',
        });
    };
    
    const handleAccountFormChange = (e) => {
        const { name, value } = e.target;
        setNewAccountData({ ...newAccountData, [name]: value });
    };

    const handleTransactionFormChange = (e) => {
        const { name } = e.target;
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;

        if (name === 'case') {
            setNewTransactionData({ ...newTransactionData, [name]: value, invoice: '' });
            return;
        }

        if (name === 'transaction_type') {
            // Reset disbursement-only fields when switching back to deposit
            if (value === 'DEPOSIT') {
                setNewTransactionData({
                    ...newTransactionData,
                    transaction_type: value,
                    case: '',
                    invoice: '',
                    is_third_party_disbursement: false,
                    third_party_invoice_number: '',
                    third_party_supplier_name: '',
                    client_tin: '',
                    client_address: '',
                });
                return;
            }
        }

        if (name === 'is_third_party_disbursement') {
            // Third-party disbursements should not require paying an internal invoice
            setNewTransactionData({
                ...newTransactionData,
                [name]: value,
                invoice: value ? '' : newTransactionData.invoice,
            });
            return;
        }

        setNewTransactionData({ ...newTransactionData, [name]: value });
    };

    const handleAccountSubmit = async (e) => {
        e.preventDefault();
        try {
            await authAxios.post('trust-accounts/', newAccountData);
            showFormMessage('success', 'Trust account created successfully!');
            fetchTrustAccounts();
            setShowAccountForm(false);
            setNewAccountData({ client: '', account_name: '', account_number: '', bank_name: '' });
        } catch (error) {
            console.error("Error creating trust account:", error.response.data);
            showFormMessage('danger', `Failed to create trust account: ${JSON.stringify(error.response.data)}`);
        }
    };

    const handleTransactionSubmit = async (e) => {
        e.preventDefault();

        const isWithdrawal = newTransactionData.transaction_type === 'WITHDRAWAL';
        const isThirdParty = !!newTransactionData.is_third_party_disbursement;

        if (isWithdrawal && !isThirdParty && !newTransactionData.invoice) {
            showFormMessage('danger', 'An invoice must be selected for a withdrawal.');
            return;
        }

        if (isWithdrawal && isThirdParty) {
            if (!newTransactionData.third_party_invoice_number) {
                showFormMessage('danger', 'Third-party invoice number is required for a disbursement (Group K).');
                return;
            }
            if (!newTransactionData.client_tin) {
                showFormMessage('danger', 'Client TIN is required for a disbursement (Group K).');
                return;
            }
            if (!newTransactionData.client_address) {
                showFormMessage('danger', 'Client address is required for a disbursement (Group K).');
                return;
            }
        }

        try {
            const transactionPayload = { ...newTransactionData };
            // Ensure we don't send an empty string for the invoice
            if (!transactionPayload.invoice) {
                delete transactionPayload.invoice;
            }

            const transactionResponse = await authAxios.post('trust-transactions/', transactionPayload);
            const createdTransaction = transactionResponse.data;

            let successMessage = 'Transaction recorded successfully!';
            
            if (newTransactionData.transaction_type === 'WITHDRAWAL' && createdTransaction.payment_id) {
                await fetchInvoices();
                await fetchPayments();
                successMessage += ' Payment automatically applied to the selected invoice.';
            }

            showFormMessage('success', successMessage);
            
            // Refetch the single account to get updated balance and transaction list
            const response = await authAxios.get(`trust-accounts/${selectedAccount.id}/`);
            const updatedAccount = response.data;

            // Update the main list of accounts
            setTrustAccounts(prevAccounts =>
                prevAccounts.map(acc =>
                    acc.id === updatedAccount.id ? updatedAccount : acc
                )
            );

            // Reselect the account, which will trigger running balance calculation
            handleAccountSelect(updatedAccount);

            setShowTransactionForm(false);
            setNewTransactionData({
                trust_account: selectedAccount.id,
                case: '',
                invoice: '',
                transaction_type: 'DEPOSIT',
                amount: '',
                description: '',
                is_third_party_disbursement: false,
                third_party_invoice_number: '',
                third_party_supplier_name: '',
                client_tin: '',
                client_address: '',
            });
        } catch (error) {
            console.error("Error creating transaction:", error.response.data);
            showFormMessage('danger', `Failed to create transaction: ${JSON.stringify(error.response.data)}`);
        }
    };

    // --- Reverse transaction handler ---
    const [reversingId, setReversingId] = useState(null);
    const [reverseReason, setReverseReason] = useState('');
    const [showReverseModal, setShowReverseModal] = useState(null); // tx id or null

    const handleReverse = async (txId) => {
        if (!reverseReason.trim()) {
            showFormMessage('danger', 'Please provide a reason for the reversal.');
            return;
        }
        try {
            setReversingId(txId);
            await reverseTrustTransaction(authAxios, txId, reverseReason.trim());
            showFormMessage('success', 'Transaction reversed successfully.');
            setShowReverseModal(null);
            setReverseReason('');
            // Refresh the account ledger
            const resp = await authAxios.get(`trust-accounts/${selectedAccount.id}/`);
            handleAccountSelect(resp.data);
        } catch (err) {
            const detail = err?.response?.data?.detail || 'Failed to reverse transaction.';
            showFormMessage('danger', detail);
        } finally {
            setReversingId(null);
        }
    };

    const [showReconciliation, setShowReconciliation] = useState(false);
    const [reconBankBalance, setReconBankBalance] = useState('');

    // --- PDF export handler ---
    const [exportingPdf, setExportingPdf] = useState(false);

    const handleExportPdf = async () => {
        if (!selectedAccount) return;
        try {
            setExportingPdf(true);
            const params = {};
            if (ledgerDateFilter.enabled && ledgerDateFilter.startDate) params.start_date = ledgerDateFilter.startDate;
            if (ledgerDateFilter.enabled && ledgerDateFilter.endDate) params.end_date = ledgerDateFilter.endDate;
            const blob = await exportTrustLedgerPDF(authAxios, selectedAccount.id, params);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `trust-ledger-${selectedAccount.id}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            showFormMessage('success', 'Trust ledger PDF downloaded.');
        } catch (err) {
            showFormMessage('danger', 'Failed to export trust ledger PDF.');
        } finally {
            setExportingPdf(false);
        }
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Trust Accounting</h2>
            
            {/* --- MODIFIED LAYOUT --- */}
            {/* We now conditionally show EITHER the account list OR the ledger */}
            
            {selectedAccount ? (
                // --- TRANSACTION LEDGER (DEDICATED VIEW) ---
                <div className="w-full">
                    <div className="bg-white shadow-md rounded-lg">
                        <div className="p-4 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center">
                            <div>
                                <h4 className="text-xl font-bold text-gray-800">{selectedAccount.account_name} - Ledger</h4>
                                <h5 className="text-lg font-semibold text-green-600">Current Balance: {formatCurrency(selectedAccount.balance)}</h5>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2 mt-3 sm:mt-0">
                                <button
                                    className={`${showReconciliation ? 'bg-amber-600 hover:bg-amber-700' : 'bg-amber-500 hover:bg-amber-600'} text-white px-4 py-2 rounded-md text-sm transition duration-150`}
                                    onClick={() => setShowReconciliation(v => !v)}
                                >
                                    {showReconciliation ? 'Close Reconciliation' : 'Reconcile'}
                                </button>
                                <button
                                    className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 transition duration-150 disabled:opacity-50"
                                    onClick={handleExportPdf}
                                    disabled={exportingPdf}
                                >
                                    {exportingPdf ? 'Exporting…' : 'Export PDF'}
                                </button>
                                {typeof navigateToAuditSupportTrustSummary === 'function' && (
                                    <button
                                        className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm hover:bg-indigo-700 transition duration-150"
                                        onClick={() => navigateToAuditSupportTrustSummary({ reportKey: 'trust_summary' })}
                                    >
                                        View Trust Account Summary
                                    </button>
                                )}
                                <button className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm hover:bg-gray-300 transition duration-150" onClick={() => setSelectedAccount(null)}>
                                    &larr; Back to Accounts
                                </button>
                            </div>
                        </div>
                        <div className="p-4">
                            {/* Trust Reconciliation Panel */}
                            {showReconciliation && (
                              <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                                <h5 className="font-semibold text-amber-800 mb-3">Trust Account Reconciliation</h5>
                                {(() => {
                                  const txs = displayedTransactions || [];
                                  const deposits = txs.filter(t => t.transaction_type === 'DEPOSIT' && !t.is_reversed);
                                  const withdrawals = txs.filter(t => t.transaction_type === 'WITHDRAWAL' && !t.is_reversed);
                                  const totalDeposits = deposits.reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
                                  const totalWithdrawals = withdrawals.reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
                                  const ledgerBalance = parseFloat(selectedAccount.balance) || 0;
                                  const bankBal = reconBankBalance !== '' ? parseFloat(reconBankBalance) : null;
                                  const difference = bankBal !== null ? bankBal - ledgerBalance : null;
                                  return (
                                    <div className="space-y-3">
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                        <div className="bg-white p-3 rounded border">
                                          <p className="text-xs text-gray-500 uppercase">Total Deposits</p>
                                          <p className="text-lg font-bold text-green-700">{formatCurrency(totalDeposits)}</p>
                                          <p className="text-xs text-gray-400">{deposits.length} transactions</p>
                                        </div>
                                        <div className="bg-white p-3 rounded border">
                                          <p className="text-xs text-gray-500 uppercase">Total Withdrawals</p>
                                          <p className="text-lg font-bold text-red-700">{formatCurrency(totalWithdrawals)}</p>
                                          <p className="text-xs text-gray-400">{withdrawals.length} transactions</p>
                                        </div>
                                        <div className="bg-white p-3 rounded border">
                                          <p className="text-xs text-gray-500 uppercase">Ledger Balance</p>
                                          <p className="text-lg font-bold text-blue-700">{formatCurrency(ledgerBalance)}</p>
                                        </div>
                                        <div className="bg-white p-3 rounded border">
                                          <p className="text-xs text-gray-500 uppercase">Difference</p>
                                          {difference !== null ? (
                                            <p className={`text-lg font-bold ${Math.abs(difference) < 0.01 ? 'text-green-700' : 'text-red-700'}`}>
                                              {formatCurrency(difference)}
                                            </p>
                                          ) : <p className="text-sm text-gray-400">Enter bank balance</p>}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <label className="text-sm font-medium text-gray-700">Bank Statement Balance:</label>
                                        <input
                                          type="number" step="0.01"
                                          className="w-48 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-amber-500 focus:border-amber-500"
                                          placeholder="Enter bank balance"
                                          value={reconBankBalance}
                                          onChange={e => setReconBankBalance(e.target.value)}
                                        />
                                        {difference !== null && Math.abs(difference) < 0.01 && (
                                          <span className="text-green-600 text-sm font-semibold">✓ Reconciled</span>
                                        )}
                                        {difference !== null && Math.abs(difference) >= 0.01 && (
                                          <span className="text-red-600 text-sm font-semibold">⚠ Discrepancy of {formatCurrency(Math.abs(difference))}</span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                            <div className="mb-4 bg-gray-50 border border-gray-200 rounded-lg p-3">
                                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
                                    <div className="flex items-center gap-2">
                                        <input
                                            id="ledger-date-filter-enabled"
                                            type="checkbox"
                                            className="h-4 w-4"
                                            checked={ledgerDateFilter.enabled}
                                            onChange={(e) => setLedgerDateFilter(prev => ({ ...prev, enabled: e.target.checked }))}
                                        />
                                        <label htmlFor="ledger-date-filter-enabled" className="text-sm text-gray-700">
                                            Filter ledger by date range
                                        </label>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 w-full lg:w-auto">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600">Start</label>
                                            <input
                                                type="date"
                                                className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm"
                                                value={ledgerDateFilter.startDate}
                                                onChange={(e) => setLedgerDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
                                                disabled={!ledgerDateFilter.enabled}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600">End</label>
                                            <input
                                                type="date"
                                                className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm"
                                                value={ledgerDateFilter.endDate}
                                                onChange={(e) => setLedgerDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
                                                disabled={!ledgerDateFilter.enabled}
                                            />
                                        </div>
                                        <div className="flex items-end">
                                            <button
                                                type="button"
                                                className="w-full bg-white border border-gray-300 text-gray-700 px-3 py-2 rounded-md text-sm hover:bg-gray-100"
                                                onClick={() => setLedgerDateFilter({ enabled: false, startDate: '', endDate: '' })}
                                            >
                                                Clear
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {ledgerDateFilter.enabled && ledgerDateFilter.startDate && ledgerDateFilter.endDate && (
                                    <div className="mt-2 text-xs text-gray-600">
                                        Showing transactions from <span className="font-medium">{ledgerDateFilter.startDate}</span> to <span className="font-medium">{ledgerDateFilter.endDate}</span>.
                                    </div>
                                )}
                            </div>

                            {permissions.isPartner && (
                            <button className="bg-green-600 text-white px-4 py-2 rounded-md mb-4 hover:bg-green-700 shadow-sm transition duration-150" onClick={() => setShowTransactionForm(!showTransactionForm)}>
                                {showTransactionForm ? 'Cancel' : 'New Transaction'}
                            </button>
                            )}
                            {showTransactionForm && permissions.isPartner && (
                                <div className="bg-gray-50 p-4 rounded-lg mb-4 border border-gray-200 max-w-lg">
                                    <form onSubmit={handleTransactionSubmit} className="space-y-4">
                                            <div>
                                            <label htmlFor="transaction_type" className="block text-sm font-medium text-gray-700">Type</label>
                                            <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" name="transaction_type" value={newTransactionData.transaction_type} onChange={handleTransactionFormChange}>
                                                <option value="DEPOSIT">Deposit (Credit)</option>
                                                <option value="WITHDRAWAL">Withdrawal (Debit)</option>
                                            </select>
                                        </div>

                                        {newTransactionData.transaction_type === 'WITHDRAWAL' && (
                                            <div className="bg-white border border-gray-200 rounded-md p-3">
                                                <div className="flex items-start gap-2">
                                                    <input
                                                        id="is_third_party_disbursement"
                                                        name="is_third_party_disbursement"
                                                        type="checkbox"
                                                        className="h-4 w-4 mt-1"
                                                        checked={!!newTransactionData.is_third_party_disbursement}
                                                        onChange={handleTransactionFormChange}
                                                    />
                                                    <div>
                                                        <label htmlFor="is_third_party_disbursement" className="text-sm font-medium text-gray-700">
                                                            Third-party disbursement (Group K)
                                                        </label>
                                                        <p className="text-xs text-gray-500">
                                                            Records a BURS Group “K” disbursement; requires client TIN/address and third-party invoice reference.
                                                        </p>
                                                    </div>
                                                </div>

                                                {newTransactionData.is_third_party_disbursement && (
                                                    <div className="mt-3 space-y-3">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700">Third-party Invoice Number</label>
                                                            <input
                                                                type="text"
                                                                name="third_party_invoice_number"
                                                                className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm"
                                                                value={newTransactionData.third_party_invoice_number}
                                                                onChange={handleTransactionFormChange}
                                                                required
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700">Supplier Name (Optional)</label>
                                                            <input
                                                                type="text"
                                                                name="third_party_supplier_name"
                                                                className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm"
                                                                value={newTransactionData.third_party_supplier_name}
                                                                onChange={handleTransactionFormChange}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700">Client TIN</label>
                                                            <input
                                                                type="text"
                                                                name="client_tin"
                                                                className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm"
                                                                value={newTransactionData.client_tin}
                                                                onChange={handleTransactionFormChange}
                                                                required
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700">Client Address</label>
                                                            <textarea
                                                                name="client_address"
                                                                className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm"
                                                                value={newTransactionData.client_address}
                                                                onChange={handleTransactionFormChange}
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <div>
                                            <label htmlFor="amount" className="block text-sm font-medium text-gray-700">Amount</label>
                                            <input type="number" step="0.01" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" name="amount" value={newTransactionData.amount} onChange={handleTransactionFormChange} required />
                                        </div>
                                        <div>
                                            <label htmlFor="case" className="block text-sm font-medium text-gray-700">Associated Case (Optional)</label>
                                            <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" name="case" value={newTransactionData.case} onChange={handleTransactionFormChange}>
                                                <option value="">None</option>
                                                {cases.filter(c => c.client === selectedAccount.client).map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                                            </select>
                                        </div>
                                        {newTransactionData.transaction_type === 'WITHDRAWAL' && newTransactionData.case && !newTransactionData.is_third_party_disbursement && (
                                            <div>
                                                <label htmlFor="invoice" className="block text-sm font-medium text-gray-700">Invoice to Pay</label>
                                                <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" name="invoice" value={newTransactionData.invoice} onChange={handleTransactionFormChange} required>
                                                    <option value="">Select an Invoice to Pay</option>
                                                    {caseInvoices.map(inv => (
                                                        <option key={inv.id} value={inv.id}>
                                                            {inv.invoice_number} - Balance: {formatCurrency(inv.balance_due)}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                        <div>
                                            <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
                                            <textarea className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" name="description" value={newTransactionData.description} onChange={handleTransactionFormChange} required></textarea>
                                        </div>
                                        <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 shadow-sm transition duration-150">Record Transaction</button>
                                    </form>
                                </div>
                            )}
                            
                            {/* --- MODIFIED LEDGER TABLE --- */}
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Case</th>
                                            {/* --- NEW DEBIT/CREDIT COLUMNS --- */}
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Debit (-)</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Credit (+)</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Running Balance</th>
                                            {permissions.isPartner && <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {displayedTransactions.map(tx => {
                                            const isDeposit = tx.transaction_type === 'DEPOSIT';
                                            const amountFormatted = formatCurrency(tx.amount);

                                            return (
                                                <tr key={tx.id} className={tx.is_reversed ? 'bg-red-50 line-through opacity-60' : ''}>
                                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{tx.transaction_date}</td>
                                                    
                                                    <td className="px-4 py-4 text-sm text-gray-900 min-w-0" style={{maxWidth: '250px', wordBreak: 'break-word'}}>
                                                        {tx.description}
                                                        {tx.is_reversed && <span className="ml-1 text-xs text-red-600 font-semibold">(REVERSED)</span>}
                                                    </td>
                                                    <td className="px-4 py-4 text-sm text-gray-500 min-w-0" style={{maxWidth: '200px', wordBreak: 'break-word'}}>{tx.case_title || 'N/A'}</td>
                                                    
                                                    {/* --- DEBIT CELL --- */}
                                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-red-600 text-right font-medium">
                                                        {!isDeposit ? amountFormatted : '–'}
                                                    </td>
                                                    
                                                    {/* --- CREDIT CELL --- */}
                                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-green-600 text-right font-medium">
                                                        {isDeposit ? amountFormatted : '–'}
                                                    </td>
                                                    
                                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-800 text-right font-bold">
                                                        {formatCurrency(tx.running_balance)}
                                                    </td>

                                                    {permissions.isPartner && (
                                                        <td className="px-4 py-4 whitespace-nowrap text-center">
                                                            {!tx.is_reversed && showReverseModal !== tx.id && (
                                                                <button
                                                                    type="button"
                                                                    className="text-xs text-red-600 hover:text-red-800 font-medium"
                                                                    onClick={() => { setShowReverseModal(tx.id); setReverseReason(''); }}
                                                                >
                                                                    Reverse
                                                                </button>
                                                            )}
                                                            {showReverseModal === tx.id && (
                                                                <div className="flex flex-col items-center gap-1">
                                                                    <input
                                                                        type="text"
                                                                        className="border border-gray-300 rounded px-2 py-1 text-xs w-40"
                                                                        placeholder="Reason for reversal"
                                                                        value={reverseReason}
                                                                        onChange={(e) => setReverseReason(e.target.value)}
                                                                    />
                                                                    <div className="flex gap-1">
                                                                        <button
                                                                            type="button"
                                                                            className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700 disabled:opacity-50"
                                                                            disabled={reversingId === tx.id || !reverseReason.trim()}
                                                                            onClick={() => handleReverse(tx.id)}
                                                                        >
                                                                            {reversingId === tx.id ? 'Reversing…' : 'Confirm'}
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded hover:bg-gray-300"
                                                                            onClick={() => setShowReverseModal(null)}
                                                                        >
                                                                            Cancel
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {tx.is_reversed && <span className="text-xs text-gray-400">Reversed</span>}
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                // --- TRUST ACCOUNTS LIST (CENTERED VIEW) ---
                <div className="w-full max-w-2xl mx-auto">
                    <div className="bg-white shadow-md rounded-lg">
                        <div className="p-4 border-b flex justify-between items-center">
                            <h5 className="text-lg font-bold text-gray-800">Trust Accounts</h5>
                            {permissions.isPartner && (
                            <button className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-700 shadow-sm transition duration-150" onClick={() => setShowAccountForm(!showAccountForm)}>
                                {showAccountForm ? 'Close' : 'New'}
                            </button>
                            )}
                        </div>
                        {showAccountForm && permissions.isPartner && (
                            <div className="p-4 border-b">
                                <form onSubmit={handleAccountSubmit} className="space-y-4">
                                    <div>
                                        <label htmlFor="client" className="block text-sm font-medium text-gray-700">Client</label>
                                        <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" id="client" name="client" value={newAccountData.client} onChange={handleAccountFormChange} required>
                                            <option value="">Select a Client</option>
                                            {(clients || []).map(client => (
                                                <option key={client.id} value={client.id}>{getClientDisplayName(client)}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="account_name" className="block text-sm font-medium text-gray-700">Account Name</label>
                                        <input type="text" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" id="account_name" name="account_name" value={newAccountData.account_name} onChange={handleAccountFormChange} required />
                                    </div>
                                    <div>
                                        <label htmlFor="account_number" className="block text-sm font-medium text-gray-700">Account Number</label>
                                        <input type="text" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" id="account_number" name="account_number" value={newAccountData.account_number} onChange={handleAccountFormChange} required />
                                    </div>
                                    <div>
                                        <label htmlFor="bank_name" className="block text-sm font-medium text-gray-700">Bank Name</label>
                                        <input type="text" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" id="bank_name" name="bank_name" value={newAccountData.bank_name} onChange={handleAccountFormChange} required />
                                    </div>
                                    <button type="submit" className="w-full bg-green-600 text-white py-2 rounded-md hover:bg-green-700 shadow-sm transition duration-150">Create</button>
                                </form>
                            </div>
                        )}
                        <ul className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                            {isLoading ? <li className="p-4 text-center text-gray-500">Loading...</li> : 
                                trustAccounts.map(account => (
                                    <li key={account.id} className={`p-4 cursor-pointer hover:bg-gray-50 ${selectedAccount?.id === account.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`} onClick={() => handleAccountSelect(account)}>
                                        <div className="font-semibold text-gray-800">{account.account_name}</div>
                                        <div className="text-sm text-gray-600">({account.client_name})</div>
                                        <div className="text-sm text-gray-500">{account.account_number}</div>
                                    </li>
                                ))
                            }
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrustAccounting;

